/**
 * Auto-Fabricator (M3) — what the workshop should build next. Pure, no DOM.
 *
 * WHY THIS UPGRADE EXISTS: marks come from crafting. Measured on a late-game
 * character, tech gear + artifact unlocks + artifact crafts are 111 of 157
 * marks — 71%. Auto-Raid Protocol automates the raid and nothing else, so a
 * hands-off character earned under a third of a tour's marks, and
 * `succession-doctrine` (which promotes on the marks RATE) was automating that
 * trickle. Raid → craft → marks → promote is the loop; this is the second step.
 *
 * ONE DECISION PER CALL, deliberately. The caller crafts it and comes back next
 * tick, so a deep component tree is walked one item at a time and the player
 * watches it build rather than finding it done. It also means a craft that
 * fails for a reason this module cannot see costs one tick, not a loop.
 *
 * PRIORITY, cheapest-marks-first:
 *   1. unlock   — an owned blueprint for a design not yet unlocked. Free: costs
 *                 the blueprint, no materials, no credits, and unlocks are the
 *                 single largest marks line (75 of the 157 above).
 *   2. artifact — an unlocked, faction-legal design this character has not
 *                 crafted. Distinct designs only: a duplicate earns no marks.
 *   3. gear     — tech gear this character does not own, or the deepest
 *                 component it still needs. Components are not marks
 *                 themselves; they are the only route to gear that is.
 *
 * WHAT IT WILL NOT DO, and each of these is a decision rather than an omission:
 *   - craft duplicates (no marks, and it would drain materials forever)
 *   - craft consumables (the player's call — they are spent, not accumulated)
 *   - craft toward a target it cannot finish (it takes the deepest step that is
 *     craftable NOW, so it never strands materials in a half-built tree)
 */

import { gameState } from './gameState-core.js';
import { getAllArtifacts } from './util.js';
import { getGearMatrix } from './gear-core.js';
import { getRecipeById, canCraftRecipe, craftCreditCost } from './recipes-core.js';
import { getUpgradeRank, isArtifactUnlocked, ownsArtifact, ownsTechGear, duplicateCraftsAllowed } from './command-core.js';

export const AUTO_FABRICATOR_ID = 'auto-fabricator';

export function isAutoFabricatorOwned() {
    return getUpgradeRank(AUTO_FABRICATOR_ID) > 0;
}

/** Blueprint item id for a design, matching `unlockArtifactCore`. */
const blueprintFor = (artifactId) => `${artifactId}-blueprint`;

/** Faction-legal for this character? Neutral designs are legal for everyone. */
const factionAllows = (character, thing) =>
    !thing?.faction || thing.faction === 'neutral' || thing.faction === character?.faction;

/**
 * The deepest step toward `targetId` that can be crafted right now, or null.
 *
 * Deepest-first is the order a player crafts in, and the only order that makes
 * progress: a tech gear recipe's inputs are themselves crafted components, so
 * checking only the target would report "not craftable" forever while the
 * materials to build its parts sat in the inventory.
 */
export function deepestCraftableStep(targetId, character, seen = new Set()) {
    if (seen.has(targetId)) return null;      // cycle guard, same as getCraftingTree
    seen.add(targetId);

    const recipe = getRecipeById(targetId);
    if (!recipe) return null;                 // a raw drop: nothing to craft

    // Try the inputs first. If any input is short AND craftable, that is the
    // step to take now.
    for (const input of recipe.inputs) {
        const have = character?.inventory?.[input.id] || 0;
        if (have >= input.qty) continue;
        const step = deepestCraftableStep(input.id, character, seen);
        if (step) return step;
    }

    return canCraftRecipe(recipe, character) ? targetId : null;
}

/**
 * What to fabricate next, or null when there is nothing worth doing.
 *
 * @param {object} character
 * @param {number} credits
 * @returns {{kind: 'unlock'|'artifact'|'gear', id: string, cost?: number}|null}
 */
export function nextFabrication(character = gameState.character, credits = gameState.player?.credits ?? 0) {
    if (!character) return null;
    const allowDuplicates = duplicateCraftsAllowed();

    // 1. Unlocks. No materials, no credits — only a blueprint the character is
    //    already carrying, which is otherwise dead weight in the inventory.
    for (const artifact of getAllArtifacts()) {
        if (!factionAllows(character, artifact)) continue;
        if (isArtifactUnlocked(artifact.id)) continue;
        if ((character.inventory?.[blueprintFor(artifact.id)] || 0) < 1) continue;
        return { kind: 'unlock', id: artifact.id };
    }

    // 2. Artifacts the character has not built. Materials only.
    for (const artifact of getAllArtifacts()) {
        if (!factionAllows(character, artifact)) continue;
        if (!isArtifactUnlocked(artifact.id)) continue;
        if (!allowDuplicates && ownsArtifact(character, artifact.id)) continue;
        const recipe = getRecipeById(artifact.id);
        if (recipe && canCraftRecipe(recipe, character)) {
            return { kind: 'artifact', id: artifact.id };
        }
    }

    // 3. Tech gear, and the components it is made of.
    //
    //    Walked via `getGearMatrix`, which is the character's OWN faction gear
    //    in the order the Gear page shows it — the fabricator then builds the
    //    kit in the order the player already reads it, and the order is stable
    //    across reloads. A flat item scan would not do: tech gear items carry
    //    no `faction` field (it is encoded by which catalog array they sit in),
    //    so scanning every item would happily spend materials on gear this
    //    character can never equip.
    for (const { techniques } of getGearMatrix(character.faction)) {
        for (const { item } of techniques) {
            if (!item) continue;
            if (!allowDuplicates && ownsTechGear(character, item.id)) continue;
            const step = deepestCraftableStep(item.id, character);
            if (!step) continue;
            const cost = craftCreditCost(step);
            // Credits gate components (artifact crafting is free). Skipping
            // rather than halting: a cheaper target further down the matrix may
            // still be affordable, and the raid loop keeps earning either way.
            if (cost > credits) continue;
            return { kind: 'gear', id: step, cost };
        }
    }

    return null;
}
